import React, { useState, useEffect, useRef, useCallback } from 'react';
import useMessageChannel from '../../hooks/useMessageChannel';
import {
  isGroupedWithPrev,
  needsDateSeparator,
  dateSeparatorLabel,
} from '../../lib/message_text';
import ChannelHeader from './ChannelHeader';
import MessageItem from './MessageItem';
import MessageComposer from './MessageComposer';

function csrfToken() {
  const el = document.querySelector('meta[name="csrf-token"]');
  return el ? el.content : '';
}

// @context TGW-1769 メッセージペイン。全体（仮想チャンネル）は既存 /api/message/circle 系 +
//   CircleMessageChannel、チャンネル実体は /api/message/channel 系 + MessageRoomChannel に分岐する。
//   初期ロード完了後に内側の Stream をマウントする（useMessageChannel の initialMessages が
//   マウント時固定のため）。チャンネル切替は key 差し替えで丸ごと再マウント。
function endpointsFor(channel, circleId) {
  if (channel.isGeneral) {
    return {
      scope: 'circle',
      targetId: circleId,
      identifierKey: 'circle_id',
      fetch: '/api/message/circle',
      send: '/api/message/circle_all_send',
    };
  }
  return {
    scope: 'channel',
    targetId: channel.id,
    identifierKey: 'message_group_id',
    fetch: '/api/message/channel',
    send: '/api/message/channel_send',
  };
}

export default function MessagePane(props) {
  const { circleId, channel } = props;
  const [initial, setInitial] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const config = endpointsFor(channel, circleId);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(
          `${config.fetch}?${config.identifierKey}=${config.targetId}&page=1`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (!res.ok) {
          if (!aborted) setLoadError(res.status);
          return;
        }
        const data = await res.json();
        if (!aborted) setInitial(data);
      } catch (e) {
        if (!aborted) setLoadError('network');
      }
    })();
    return () => {
      aborted = true;
    };
    // channel切替はkey再マウントで扱うため依存は初回のみ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadError) {
    return (
      <div className="slackch-pane">
        <ChannelHeader {...props} channel={channel} numberOfMembers={null} />
        <div className="slackch-pane-error">
          このチャンネルにはアクセスできません
        </div>
      </div>
    );
  }
  if (!initial) {
    return (
      <div className="slackch-pane">
        <ChannelHeader {...props} channel={channel} numberOfMembers={null} />
        <div className="slackch-pane-loading">読み込み中…</div>
      </div>
    );
  }
  return <MessageStream {...props} config={config} initial={initial} />;
}

function MessageStream({
  circleId,
  channel,
  currentUser,
  canManage,
  config,
  initial,
  onOpenDrawer,
  onOpenThread,
  onOpenSettings,
  onOpenMembers,
  onThreadReplyReceived,
  onMessageDeleted,
}) {
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(!!initial.isLastPage);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // @context TGW-1769 Phase2 非プレミアムのチャンネルはBEが直近3ヶ月に制限する。
  //   履歴の先頭（isLastPage）に到達したときだけ案内を出す。
  const historyLimited = !!initial.historyLimited;
  const listRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const firstUnreadMessageId = initial.firstUnreadMessageId || 0;
  const composerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  // 子要素間の dragenter/dragleave によるちらつき防止用カウンタ
  const dragCounterRef = useRef(0);

  const {
    messages,
    sendMessage,
    retryMessage,
    isConnected,
    prependMessages,
    updateMessage,
  } = useMessageChannel({
    scope: config.scope,
    targetId: config.targetId,
    initialMessages: initial.messages || [],
    initialMaxMessageId: initial.maxMessageId || 0,
    apiEndpoints: {
      fetch: config.fetch,
      send: config.send,
    },
    identifierKey: config.identifierKey,
    currentUserId: currentUser.id,
    onThreadReply: onThreadReplyReceived,
  });

  // 新着時: 最下部付近を見ていれば追従スクロール
  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const handleScroll = useCallback(async () => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;

    if (el.scrollTop <= 0 && !isLastPage && !loadingOlder) {
      setLoadingOlder(true);
      const nextPage = page + 1;
      try {
        const res = await fetch(
          `${config.fetch}?${config.identifierKey}=${config.targetId}&page=${nextPage}`,
          {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          },
        );
        if (res.ok) {
          const data = await res.json();
          const prevHeight = el.scrollHeight;
          prependMessages(data.messages || []);
          setPage(nextPage);
          setIsLastPage(!!data.isLastPage);
          // prepend後もスクロール位置を維持する
          requestAnimationFrame(() => {
            el.scrollTop = el.scrollHeight - prevHeight;
          });
        }
      } finally {
        setLoadingOlder(false);
      }
    }
  }, [page, isLastPage, loadingOlder, config, prependMessages]);

  const handleSend = useCallback(
    ({
      body,
      attachments,
      attachmentPreviews,
      mentionUserIds,
      mentionChannel,
      mentionEveryone,
    }) => {
      stickToBottomRef.current = true;
      sendMessage({
        body,
        attachments,
        attachmentPreviews,
        extraParams: {
          mention_user_ids:
            mentionUserIds && mentionUserIds.length ? mentionUserIds : null,
          mention_channel: mentionChannel ? 'true' : null,
          mention_everyone: mentionEveryone ? 'true' : null,
        },
      });
    },
    [sendMessage],
  );

  // リアクション（5種）。like_type='' で解除。楽観的にローカル反映する。
  const handleToggleReaction = useCallback(
    async (message, likeType) => {
      const already = (message.messages_like_users || []).find(
        (l) => l.user_id === currentUser.id && l.like_type === likeType,
      );
      const nextLikeType = already ? '' : likeType;
      const likes = (message.messages_like_users || []).filter(
        (l) => l.user_id !== currentUser.id,
      );
      if (!already) {
        likes.push({
          user_id: currentUser.id,
          like_type: likeType,
          user: { id: currentUser.id },
        });
      }
      updateMessage({ id: message.id, messages_like_users: likes });

      const params = new URLSearchParams();
      params.append('message_type', channel.isGeneral ? 'circle' : 'direct');
      params.append(
        'message_type_id',
        channel.isGeneral ? circleId : channel.id,
      );
      params.append('message_id', message.id);
      params.append('like_type', nextLikeType);
      await fetch('/api/message/like', {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken() },
        credentials: 'same-origin',
        body: params,
      });
    },
    [channel, circleId, currentUser.id, updateMessage],
  );

  const handleDelete = useCallback(
    async (message) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm('このメッセージを削除しますか？')) return;
      const res = await fetch(`/api/message?message_id=${message.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken() },
        credentials: 'same-origin',
      });
      if (res.ok) {
        updateMessage({
          id: message.id,
          is_deleted: true,
          body: '削除されました',
          body_url_to_link: '削除されました',
        });
        // 開いているスレッドパネルのルートにも削除を伝播する
        if (onMessageDeleted) onMessageDeleted(message.id);
      }
    },
    [updateMessage, onMessageDeleted],
  );

  // ファイルのドラッグ&ドロップ。ファイル以外のドラッグは無視する。
  const hasFiles = (e) => !!e.dataTransfer?.types?.includes('Files');

  const handleDragEnter = useCallback((e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
  }, []);

  const handleDragLeave = useCallback((e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) composerRef.current?.addFiles(files);
  }, []);

  return (
    <div
      className="slackch-pane"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="slackch-drop-overlay">ここにファイルをドロップ</div>
      )}
      <ChannelHeader
        channel={channel}
        numberOfMembers={initial.numberOfMembers}
        canManage={canManage}
        onOpenDrawer={onOpenDrawer}
        onOpenSettings={onOpenSettings}
        onOpenMembers={onOpenMembers}
      />
      {!isConnected && (
        <div className="slackch-ws-warning">接続が切れました。再接続中…</div>
      )}
      <div
        className="slackch-message-list"
        ref={listRef}
        onScroll={handleScroll}
      >
        {loadingOlder && (
          <div className="slackch-older-loading">
            過去のメッセージを読み込み中…
          </div>
        )}
        {historyLimited && isLastPage && (
          <div className="slackch-history-limit">
            <p>このチャンネルでは直近3ヶ月のメッセージのみ表示されます。</p>
            {canManage && (
              <p>
                {/* /circle/plan/:id はオーナー以外404のため、正会員が閲覧できる購入ページへ誘導する */}
                <a href={`/circles/${circleId}/subscriptions`}>
                  プレミアムサークルにアップグレード
                </a>
                すると、すべての履歴を表示できます。
              </p>
            )}
          </div>
        )}
        {messages.length === 0 && (
          <div className="slackch-empty">まだメッセージがありません</div>
        )}
        {messages.map((message, i) => {
          const prev = i > 0 ? messages[i - 1] : null;
          return (
            <React.Fragment key={message.id || message.client_id}>
              {needsDateSeparator(message, prev) && (
                <div className="slackch-date-separator">
                  <span>{dateSeparatorLabel(message.created_at)}</span>
                </div>
              )}
              {firstUnreadMessageId > 0 &&
                message.id === firstUnreadMessageId && (
                  <div className="slackch-unread-line">
                    <span>ここから未読</span>
                  </div>
                )}
              <MessageItem
                message={message}
                currentUser={currentUser}
                canManage={canManage}
                isGrouped={isGroupedWithPrev(message, prev)}
                threadsEnabled
                onOpenThread={onOpenThread}
                onToggleReaction={handleToggleReaction}
                onDelete={handleDelete}
                onRetry={retryMessage}
                // @context TGW-1860 未読レンジ内（ここから未読以降）のみメンション黄色を出す。
                //   既読分（firstUnread より前）は出さない＝一度開けば黄色が消える。
                unread={
                  firstUnreadMessageId > 0 && message.id >= firstUnreadMessageId
                }
              />
            </React.Fragment>
          );
        })}
      </div>
      <MessageComposer
        ref={composerRef}
        circleId={circleId}
        channel={channel}
        onSend={handleSend}
        placeholder=""
      />
    </div>
  );
}
